import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { useRefresh } from '@digiwedge/hooks-auth-web';

interface JwtPayload {
  exp?: number;
}

export function RequireAuth() {
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const token = useGlobalStore((s: GlobalState) => s.token);
  const refreshToken = useGlobalStore((s: GlobalState) => s.refreshToken);
  const jti = useGlobalStore((s: GlobalState) => s.jti);
  const logout = useGlobalStore((s: GlobalState) => s.logout);
  const { refresh } = useRefresh();

  const [initDone, setInitDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        if (!token) return;
        const decoded = jwtDecode<JwtPayload>(token);
        const exp = decoded.exp ?? 0;
        const now = Math.floor(Date.now() / 1000);
        if (exp && exp <= now) {
          if (!refreshToken) {
            logout();
            return;
          }
          await refresh({ refreshToken, jti });
        }
      } catch {
        logout();
      } finally {
        if (!cancelled) setInitDone(true);
      }
    };
    void boot();
    return () => {
      cancelled = true;
    };
  }, [token, refreshToken, jti, refresh, logout]);

  if (!initDone) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

export default RequireAuth;
